import * as React from "react";
import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { useLineManualEntries, type LineManualEntry } from "@/features/lines/hooks";
import { formatDateTime } from "@/lib/display-labels";
import { ManualEntryDetailDialog } from "./ManualEntryDetailDialog";

export interface ManualEntriesCardProps {
  lineId: string;
}

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: "Concluída",
  RUNNING: "Em processamento",
  FAILED: "Falha",
  PENDING: "Pendente",
};

export function ManualEntriesCard({ lineId }: ManualEntriesCardProps): React.ReactElement {
  const entriesQuery = useLineManualEntries(lineId);
  const { data: entries = [], isLoading } = entriesQuery;
  const [selected, setSelected] = React.useState<LineManualEntry | null>(null);

  if (isLoading) {
    return (
      <Card className="p-4">
        <div className="bg-bg-muted h-16 rounded motion-safe:animate-pulse-dot" />
      </Card>
    );
  }

  if (entriesQuery.isError) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.QueryError error={entriesQuery.error} onRetry={() => void entriesQuery.refetch()} />
      </Card>
    );
  }

  if (entries.length === 0) {
    return (
      <Card className="overflow-hidden p-0">
        <Notify.Empty
          title="Sem entradas manuais"
          description="Lançamentos manuais aparecem aqui quando registrados via 'Registrar dado'."
        />
      </Card>
    );
  }

  return (
    <Card className="space-y-3 rounded-2xl px-[22px] py-5" aria-labelledby="manual-entries-card-h2">
      <header className="flex items-center justify-between gap-2">
        <h2
          id="manual-entries-card-h2"
          className="text-[14px] font-semibold tracking-tight text-text-primary"
        >
          Entradas manuais
        </h2>
        <span className="font-mono text-[11px] text-text-tertiary">{entries.length}</span>
      </header>

      <ul className="space-y-1.5">
        {entries.slice(0, 10).map((e) => (
          <li key={e.id}>
            {}
            <button
              type="button"
              onClick={() => setSelected(e)}
              className="hover:bg-bg-muted-rd/40 w-full space-y-0.5 rounded p-1 text-left text-[12px] focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
              aria-label={`Ver detalhes da entrada manual ${e.id}`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-mono text-text-primary">
                  {e.totalRows ?? "—"} linha{e.totalRows === 1 ? "" : "s"}
                </span>
                <span className="text-[11px] text-text-tertiary">
                  {STATUS_LABEL[e.status] ?? e.status}
                </span>
              </div>
              <p className="text-[11px] text-text-tertiary">
                {e.referenceAt
                  ? `Ref.: ${formatDateTime(e.referenceAt)}`
                  : `Registrada em ${formatDateTime(e.createdAt)}`}
              </p>
            </button>
          </li>
        ))}
      </ul>

      {entries.length > 10 ? (
        <p className="text-[11px] text-text-tertiary">
          + {entries.length - 10} entrada(s) adicional(is).
        </p>
      ) : null}

      <ManualEntryDetailDialog
        entry={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </Card>
  );
}
